// @ts-nocheck
/**
 * Agent Activity modal — 4 date-bucket presets (last 7/14/21/28 days),
 * server-fetched activity rows, accordion-per-session with ActivityTimeline
 * inside each. framer-motion animation preserved. Direct shadcn Dialog usage.
 */
import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Activity, CalendarClock, ChevronDown, ListChecks, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils/cn';
import { getApiCall } from '@/service/httpCall';
import { API_AGENT_ACTIVITY_LOG_LIST } from '@/service/url';
import Loader from '@/components/partials/components/Loader';
import ActivityTimeline from './ActivityTimeline';

const DATE_BUCKETS = [
  { key: '0-7', label: 'Last 7 days', from: 0, to: 6 },
  { key: '7-14', label: '7–14 days', from: 7, to: 13 },
  { key: '14-21', label: '14–21 days', from: 14, to: 20 },
  { key: '21-28', label: '21–28 days', from: 21, to: 27 },
];

const PAGE_LIMIT = 200;

const fmtDateTime = (v: any) => (v ? moment(v).format('DD MMM YYYY · HH:mm') : '—');

const bucketRange = (bucket: any) => ({
  start_date: moment().subtract(bucket.to, 'days').format('YYYY-MM-DD'),
  end_date: moment().subtract(bucket.from, 'days').format('YYYY-MM-DD'),
});

const AgentActivityModal = ({ show, onHide, agentId, agentName, agentNumber }: any) => {
  const [bucketKey, setBucketKey] = useState(DATE_BUCKETS[0].key);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);

  const fetchData = useCallback(
    async (key: string) => {
      if (!agentId) return;
      const bucket = DATE_BUCKETS.find((b) => b.key === key) || DATE_BUCKETS[0];
      const { start_date, end_date } = bucketRange(bucket);
      setLoading(true);
      setSelectedSession(null);
      try {
        const params = new URLSearchParams({
          start_date,
          end_date,
          agent_id: String(agentId),
          page: '1',
          limit: String(PAGE_LIMIT),
        });
        const resp: any = await getApiCall(
          `${API_AGENT_ACTIVITY_LOG_LIST}?${params.toString()}`,
          {},
        );
        setRows(Array.isArray(resp?.rows) ? resp.rows : []);
      } catch (e) {
        toast.error(typeof e === 'string' ? e : 'Failed to load activity');
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [agentId],
  );

  useEffect(() => {
    if (show && agentId) {
      setBucketKey(DATE_BUCKETS[0].key);
      fetchData(DATE_BUCKETS[0].key);
    }
    if (!show) {
      setRows([]);
      setSelectedSession(null);
    }
  }, [show, agentId, fetchData]);

  const onBucketChange = (key: string) => {
    setBucketKey(key);
    fetchData(key);
  };

  const title = agentName || `Agent #${agentId}`;

  return (
    <Dialog open={!!show} onOpenChange={(o) => !o && onHide?.()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
              <User className="h-5 w-5" strokeWidth={2.3} />
            </span>
            <div className="min-w-0">
              <DialogTitle className="text-base">{title}</DialogTitle>
              <div className="text-xs font-medium text-muted-foreground">
                Agent activity{agentNumber ? ` · ${agentNumber}` : ''}
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Date bucket switcher — each tab is its own embossed pill so
            inactive ones stay legible against any modal body colour. */}
        <div className="flex flex-wrap items-center gap-2">
          {DATE_BUCKETS.map((b) => {
            const active = b.key === bucketKey;
            const { start_date, end_date } = bucketRange(b);
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => onBucketChange(b.key)}
                disabled={loading}
                title={`${start_date} → ${end_date}`}
                className={cn(
                  'inline-flex items-center rounded-md px-3.5 py-1.5 text-xs font-semibold ring-1 ring-inset transition-all cursor-pointer',
                  active
                    ? 'bg-primary text-primary-foreground ring-primary/40 shadow-[3px_3px_6px_rgba(163,177,198,0.4),-3px_-3px_6px_rgba(255,255,255,0.9)]'
                    : 'bg-card text-foreground ring-border/60 shadow-[3px_3px_6px_rgba(163,177,198,0.35),-3px_-3px_6px_rgba(255,255,255,0.9)] hover:bg-accent hover:text-accent-foreground',
                  loading && 'cursor-not-allowed opacity-60',
                )}
              >
                {b.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <Loader />
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2.5 px-5 py-10 text-center">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
              <CalendarClock className="h-6 w-6" strokeWidth={2} />
            </span>
            <div className="text-sm font-medium text-muted-foreground">
              No activity found for this period.
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {rows.map((row: any) => {
              const count =
                row.activity_count ??
                (Array.isArray(row.activity) ? row.activity.length : 0);
              const isOpen = selectedSession?.id === row.id;
              return (
                <div
                  key={row.id}
                  className={cn(
                    'overflow-hidden rounded-xl border transition-all',
                    isOpen
                      ? 'border-primary/30 shadow-md'
                      : 'border-border shadow-sm hover:border-border/80',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedSession(isOpen ? null : row)}
                    className="flex w-full items-center gap-3.5 px-4 py-3.5 text-left"
                  >
                    <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                      <ListChecks className="h-4 w-4" strokeWidth={2.2} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold">{row.date || '—'}</div>
                      <div className="text-xs font-medium text-muted-foreground">
                        Login {fmtDateTime(row.login_time)}
                      </div>
                    </div>
                    <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-xs font-bold text-primary">
                      <Activity className="h-3 w-3" strokeWidth={2.6} />
                      {count}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="inline-flex flex-shrink-0 text-muted-foreground"
                    >
                      <ChevronDown className="h-4 w-4" strokeWidth={2.4} />
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-border px-4 pb-4 pt-3.5">
                          <ActivityTimeline events={row.activity} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AgentActivityModal;
